import { Tooltip, Box, List, ListItem, Chip } from "@analyticsMD/quil";
import {
  FlagOutlined,
  Circle,
  CircleOutlined,
  Check,
  InventoryOutlined,
  ListOutlined,
  Insights,
  AutoAwesomeOutlined,
  AssignmentOutlined,
} from "@mui/icons-material";
import {
  green,
  deepPurple,
  purple,
  indigo,
  grey,
  blueGrey,
} from "@mui/material/colors";
import moment from "moment";
import { Dispatch, MutableRefObject, SetStateAction } from "react";
import DispositionSelectorInline from "../components/list/disposition-selector-inline";
import EDDSelectorInline from "../components/list/edd-selector-inline";
import MedicallyReadyToggle from "../components/medically-ready-toggle";
import { DischargeOrderStatusChoices } from "../types/DischargeOrderStatusChoices";
import { IntelligenceCategories } from "../types/IntelligenceCategories";
import { PatientClasses } from "../types/PatientClasses";
import NumberBetweenOperator from "../utils/filter/number-between-operator";
import {
  LocationSortComparator,
  BedSubSortComparator,
  createCareTeamSortComparator,
  EDDBedSubSortComparator,
} from "../utils/sort/sort-comparators";
import {
  CareTeamColumnHeader,
  CareTeamSortConfig,
} from "../components/list/care-team-column-header";
import RoundingSession from "../types/RoundingSession";
import Configuration from "../types/Configuration";
import { AnalyticsBrowser } from "@segment/analytics-next";
import Disposition from "../types/Disposition";
import { css } from "@emotion/css";
import { GridColDef } from "@mui/x-data-grid";
import PatientNote from "../types/PatientNote";
import { tooltipClasses } from "@mui/material";
import {
  clinicalInsightPrimaryValue,
  clinicalInsightTimestampRaw,
  sortClinicalInsights,
} from "../utils/clinical-insight-display";

const styles = {
  headerText: css`
    color: ${grey[600]};
    font-weight: bold;
  `,
  subtext: css`
    font-size: 0.75rem;
    color: ${grey[600]};
  `,
};

const sxStyles = {
  headerIcon: {
    paddingLeft: "0.5rem",
  },
  roundingStatus: {
    height: "0.5rem",
    width: "0.5rem",
    alignSelf: "center",
  },
  multilineText: { display: "flex", flexDirection: "column" },
  inlineSelectorWrapper: {
    height: "100%",
    width: "100%",
    minWidth: 0,
    maxWidth: "100%",
    alignItems: "flex-start",
  },
  roundingIndicator: {
    display: "flex",
    height: "100%",
    paddingLeft: "0.75rem",
  },
  tooltipContent: {
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem",
    padding: "0.5rem",
  },
  tooltipPopper: {
    [`&.${tooltipClasses.popper} .${tooltipClasses.tooltip}`]: {
      marginRight: "0.25rem",
      backgroundColor: "white",
      width: "fit-content",
    },
  },
};

export const getPatientListColumns = (
  activeRoundingSession: RoundingSession,
  selectedDept: Record<string, any>,
  selectableDispositions: Disposition[],
  configuration: Configuration,
  setShowErrorToast: Dispatch<SetStateAction<boolean>>,
  analytics: AnalyticsBrowser,
  careTeamSort: CareTeamSortConfig,
  selectedPatientRef?: MutableRefObject<number>,
  listPatientDataReadOnly = false,
): GridColDef[] => {
  // Build map directly from patient_entries
  const patientRoundingStatus: Record<number, any> =
    activeRoundingSession?.patient_entries?.reduce(
      (status, entry) => {
        status[entry.patient] = {
          isRounded: entry.is_rounded,
          timestamp: entry.rounded_timestamp,
        };
        return status;
      },
      {} as Record<number, any>,
    ) || {};

  const baseColumns = [
    {
      field: "rounding_status_placeholder",
      headerName: "",
      width: 50,
      sortable: false,
    },
    {
      field: "rounding_status",
      width: 50,
      align: "center",
      headerAlign: "center",
      sortable: false,
      valueGetter: (value, row) =>
        patientRoundingStatus[row.id]?.isRounded ? "Yes" : "No",
      renderHeader: (params) => (
        <Tooltip title="Rounded On" arrow>
          <FlagOutlined sx={sxStyles.headerIcon} />
        </Tooltip>
      ),
      renderCell: (params) => {
        const roundingStatus = patientRoundingStatus[params.row.id];
        return roundingStatus?.isRounded ? (
          <Tooltip
            title={`Discharge Plan Discussed ${moment(
              roundingStatus.timestamp,
            ).fromNow()}`}
            arrow
          >
            <Box sx={sxStyles.roundingIndicator}>
              <Circle color="primary" sx={sxStyles.roundingStatus} />
            </Box>
          </Tooltip>
        ) : (
          <Box sx={sxStyles.roundingIndicator}>
            <CircleOutlined color="primary" sx={sxStyles.roundingStatus} />
          </Box>
        );
      },
    },
    {
      field: "location",
      headerName: "Location",
      display: "flex",
      width: 100,
      valueGetter: (value, row) => ({
        current_department: row.current_department,
        current_bed: row.current_bed,
      }),
      valueFormatter: (value, row) =>
        `${row.current_department} ${row.current_bed}`,
      renderCell: (params) => {
        return (
          <Box sx={sxStyles.multilineText}>
            <span>{params.row.current_department}</span>
            <span className={styles.subtext}>{params.row.current_bed}</span>
          </Box>
        );
      },
      sortComparator: LocationSortComparator,
    },
    {
      field: "patient_name",
      headerName: "Patient",
      display: "flex",
      width: 185,
      valueGetter: (value, row) =>
        `${row.first_name || ""} ${
          row.middle_name ? row.middle_name.charAt(0) : ""
        } ${row.last_name || ""} (${row.age}) ${row.mrn || row.emr_patient_id}`,
      renderCell: (params) => {
        const name = `${params.row.first_name || ""} ${
          params.row.middle_name ? params.row.middle_name.charAt(0) : ""
        } ${params.row.last_name || ""} (${params.row.age})`;

        return (
          <Box sx={sxStyles.multilineText}>
            <span>{name}</span>
            <span className={styles.subtext}>
              {params.row.mrn || params.row.emr_patient_id}
            </span>
          </Box>
        );
      },
    },
    {
      field: "patient_class",
      headerName: "Class",
      display: "flex",
      width: 100,
      valueFormatter: (value, row) => {
        const patientClassMap = {
          [PatientClasses.EMERGENCY.toUpperCase()]: "ED",
          [PatientClasses.INPATIENT.toUpperCase()]: "IP",
          [PatientClasses.OBSERVATION.toUpperCase()]: "OBS",
          [PatientClasses.OUTPATIENT.toUpperCase()]: "OP",
          [PatientClasses.OTHER.toUpperCase()]: "OTR",
          [PatientClasses.UNKNOWN.toUpperCase()]: "UNK",
        };
        return value
          ? patientClassMap[value.toUpperCase()]
          : patientClassMap[PatientClasses.UNKNOWN.toUpperCase()];
      },
      sortComparator: BedSubSortComparator,
    },
    {
      field: "care_team",
      headerName: "Care Team",
      display: "flex",
      width: 150,
      sortable: false,
      hideSortIcons: true,
      valueGetter: (value, row) => ({
        attending_provider: row.attending_provider,
        service: row.service,
      }),
      valueFormatter: (value, row) =>
        `${row.attending_provider} ${row.service}`,
      renderCell: (params) => {
        return (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <span>{params.row.attending_provider}</span>
            <span className={styles.subtext}>{params.row.service}</span>
          </Box>
        );
      },
      sortComparator: createCareTeamSortComparator(careTeamSort.sortBy),
      renderHeader: () => (
        <CareTeamColumnHeader
          sortBy={careTeamSort.sortBy}
          onSortByChange={careTeamSort.setSortBy}
        />
      ),
    },
    {
      field: "payor",
      headerName: "Payor",
      display: "flex",
      width: 110,
      sortComparator: BedSubSortComparator,
    },
    {
      field: "los",
      headerName: "LOS",
      display: "flex",
      width: 95,
      valueFormatter: (value, row) => row.los,
      valueGetter: (value, row) => {
        const los = parseInt(row.los);

        if (Number.isNaN(los)) {
          return row.los;
        }

        // for days, we're done here
        if (row.los.toLowerCase().includes("d")) {
          return los;
        }

        // otherwise, we need to convert hours to days
        return los / 24;
      },
      renderHeader: (params) => {
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              alignItems: "center",
            }}
          >
            <span className={styles.headerText}>LOS</span>
            {configuration.gmlos_display_enabled && (
              <span className={styles.headerText}>(GMLOS)</span>
            )}
          </Box>
        );
      },
      renderCell: (params) => {
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "75%",
              alignItems: "center",
            }}
          >
            <span>{params.row.los}</span>
            {configuration.gmlos_display_enabled && (
              <span className={styles.subtext}>{params.row.gmlos}</span>
            )}
          </Box>
        );
      },
      filterOperators: NumberBetweenOperator,
      sortComparator: BedSubSortComparator,
    },
    {
      field: "discharge_order_status",
      headerName: "D/C Order",
      display: "flex",
      width: 110,
      valueGetter: (value, row) => {
        switch (row.discharge_order_status) {
          case DischargeOrderStatusChoices.HAS_ORDER:
            return "Has D/C Order";
          case DischargeOrderStatusChoices.HAS_CONDITIONAL_ORDER:
            return "Has conditional D/C order";
          default:
            return "Missing D/C order";
        }
      },
      renderCell: (params) => {
        const dischargeOrderTimestamp = params.row.discharge_order_timestamp
          ? moment(params.row.discharge_order_timestamp).format(
              "MM/DD [at] HH:mm",
            )
          : null;

        if (
          params.row.discharge_order_status ===
          DischargeOrderStatusChoices.HAS_ORDER
        ) {
          return (
            <Tooltip
              slotProps={{
                popper: {
                  sx: sxStyles.tooltipPopper,
                },
                tooltip: {
                  sx: { padding: "0.5rem" },
                },
              }}
              title={
                dischargeOrderTimestamp
                  ? `Active ${dischargeOrderTimestamp}`
                  : "Has D/C order"
              }
              placement="bottom"
              arrow
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  width: "75%",
                }}
              >
                <Check sx={{ color: green[800] }} />
              </Box>
            </Tooltip>
          );
        }
        if (
          params.row.discharge_order_status ===
          DischargeOrderStatusChoices.HAS_CONDITIONAL_ORDER
        ) {
          return (
            <Tooltip
              slotProps={{
                popper: {
                  sx: sxStyles.tooltipPopper,
                },
                tooltip: {
                  sx: { padding: "0.5rem" },
                },
              }}
              title={
                dischargeOrderTimestamp
                  ? `Conditional ${dischargeOrderTimestamp}`
                  : "Has conditional D/C order"
              }
              placement="bottom"
              arrow
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  width: "75%",
                }}
              >
                <InventoryOutlined sx={{ color: deepPurple[400] }} />
              </Box>
            </Tooltip>
          );
        }
        return <></>;
      },
      sortComparator: BedSubSortComparator,
    },
    {
      field: "current_edd",
      headerName: "EDD",
      display: "flex",
      width: 250,
      valueGetter: (value, row) => {
        const eddValue = row.current_edd ? row.current_edd.edd_value : null;

        if (!eddValue) {
          return { edd: "", is_am_discharge: "No" };
        }

        const formatted = moment(eddValue).format("MM/DD/YYYY a");
        return {
          edd: formatted,
          eddValue: eddValue,
          is_am_discharge: row.current_edd.is_am_discharge ? "Yes" : "No",
        };
      },
      renderCell: (params) => {
        const intelText = (params.row.intelligence_text ?? []).find(
          (i) => i.intelligence_category === IntelligenceCategories.EDD,
        ); // there should only ever be one result

        return (
          <Box sx={sxStyles.inlineSelectorWrapper}>
            <EDDSelectorInline
              currentUserEDD={params.row.current_edd}
              suggestedEDD={params.row.suggested_edd}
              intelText={intelText}
              patientID={params.row.id}
              enableDischargeTimeOfDay={
                configuration.discharge_time_of_day_enabled
              }
              analytics={analytics}
              setShowErrorToast={setShowErrorToast}
              disabled={
                listPatientDataReadOnly ||
                (selectedPatientRef && selectedPatientRef.current !== null)
              }
            />
          </Box>
        );
      },
      sortComparator: EDDBedSubSortComparator,
    },
    {
      field: "current_dispositions",
      headerName: "Disposition",
      display: "flex",
      width: 300,
      valueGetter: (value, row) => {
        const currentUserEnteredDispositions = row.current_dispositions ?? [];
        const currentDispositions = currentUserEnteredDispositions
          .map((d) => d.disposition?.display_name?.toUpperCase())
          .filter((name) => name);

        return currentDispositions.join("|");
      },
      renderCell: (params) => {
        const intelText = (params.row.intelligence_text ?? []).find(
          (i) => i.intelligence_category === IntelligenceCategories.DISPOSITION,
        ); // there should only ever be one result

        // Filter out dispositions where disposition is undefined or display_name is missing
        const validDispositions = (
          params.row.current_dispositions ?? []
        ).filter((pd) => pd.disposition && pd.disposition.display_name);

        return (
          <Box sx={sxStyles.inlineSelectorWrapper}>
            <DispositionSelectorInline
              selectableDispositions={selectableDispositions}
              currentUserEnteredPatientDispositions={validDispositions}
              suggestedDisposition={params.row.suggested_disposition}
              intelText={intelText}
              patientID={params.row.id}
              isMultiDispoEnabled={configuration.multiple_dispositions_enabled}
              analytics={analytics}
              setShowErrorToast={setShowErrorToast}
              disabled={
                listPatientDataReadOnly ||
                (selectedPatientRef && selectedPatientRef.current !== null)
              }
            />
          </Box>
        );
      },
      sortComparator: BedSubSortComparator,
    },
    {
      field: "medically_ready_status",
      headerName: "Med. Ready",
      display: "flex",
      width: 110,
      valueGetter: (value, row) =>
        row.medically_ready_status ? row.medically_ready_status.status : "No",
      renderCell: (params) => {
        return (
          <MedicallyReadyToggle
            medicallyReadyStatus={params.row.medically_ready_status}
            patientID={params.row.id}
            disabled={
              listPatientDataReadOnly ||
              configuration.medically_ready_read_only ||
              (selectedPatientRef && selectedPatientRef.current !== null)
            }
            setShowErrorToast={setShowErrorToast}
          />
        );
      },
      sortComparator: BedSubSortComparator,
    },
    {
      field: "milestones",
      headerName: "Milestones",
      display: "flex",
      width: 100,
      sortable: false,
      valueGetter: (value, row) => {
        return row.milestones.map((i) =>
          i.milestone.display_name.toUpperCase(),
        );
      },
      renderCell: (params) => {
        // only display milestones, not clinical markers
        const milestones = params.row.milestones.filter(
          (m) => !m.milestone.is_clinical_marker,
        );

        const incomplete = milestones.sort((a, b) =>
          a.milestone.sort_order === b.milestone.sort_order
            ? a.milestone.display_name.localeCompare(b.milestone.display_name)
            : a.milestone.sort_order - b.milestone.sort_order,
        );

        return incomplete.length > 0 ? (
          <Tooltip
            slotProps={{
              popper: {
                sx: sxStyles.tooltipPopper,
              },
            }}
            title={
              <Box sx={sxStyles.tooltipContent}>
                <span>Incomplete milestones:</span>
                <List
                  sx={{
                    listStyleType: "disc",
                    paddingLeft: "1rem",
                    paddingTop: "0.25rem",
                  }}
                >
                  {incomplete.map((i) => (
                    <ListItem
                      key={i.milestone.id}
                      sx={{
                        display: "list-item",
                        paddingBottom: 0,
                        paddingTop: 0,
                      }}
                      disableGutters
                    >
                      {i.milestone.display_name}
                    </ListItem>
                  ))}
                </List>
              </Box>
            }
            arrow
          >
            <Chip
              icon={
                <ListOutlined
                  sx={{
                    height: "1rem",
                    width: "1rem",
                    color: `${purple[800]} !important`,
                  }}
                />
              }
              label={incomplete.length}
              sx={{
                backgroundColor: purple[50],
                color: purple[800],
                padding: "0.25rem 0.5rem 0.25rem 0.5rem",
              }}
            />
          </Tooltip>
        ) : (
          <></>
        );
      },
    },
    {
      field: "notes",
      headerName: "Notes",
      display: "flex",
      width: 100,
      sortable: false,
      renderCell: (params) => {
        const notes: PatientNote[] = params.row.notes;

        return notes.length > 0 ? (
          <Tooltip
            slotProps={{
              popper: {
                sx: sxStyles.tooltipPopper,
              },
            }}
            title={
              <Box sx={sxStyles.tooltipContent}>
                <span className={css({ fontWeight: "bold" })}>
                  {notes[0].created_by_name} (
                  {moment(notes[0].qv_created_ts).fromNow()})
                </span>
                <span>{notes[0].content}</span>
                {notes.length > 1 && <span>+{notes.length - 1} more</span>}
              </Box>
            }
            arrow
          >
            <Chip
              icon={
                <AssignmentOutlined
                  sx={{
                    height: "1rem",
                    width: "1rem",
                    color: `${blueGrey[600]} !important`,
                  }}
                />
              }
              label={notes.length}
              sx={{
                backgroundColor: blueGrey[50],
                color: blueGrey[600],
                padding: "0.25rem 0.5rem 0.25rem 0.5rem",
              }}
            />
          </Tooltip>
        ) : (
          <></>
        );
      },
    },
    {
      field: "insights",
      headerName: "Insights",
      display: "flex",
      width: 100,
      sortable: false,
      renderCell: (params) => {
        if (params.row.insights) {
          const notes = params.row.insights.notes || [];

          const insightTypes: Record<string, number> = notes.reduce(
            (typeMap, note) => {
              const insTypes = note.quotes.map((q) => q.insight_type);

              insTypes.forEach((ins) => {
                typeMap[ins] = typeMap[ins] ? typeMap[ins] + 1 : 1;
              });

              return typeMap;
            },
            {} as Record<string, number>,
          );

          const quoteCount = notes.reduce(
            (sum, note) => sum + note.quotes.length,
            0,
          );

          if (quoteCount === 0) {
            return <></>;
          }

          return (
            <Tooltip
              slotProps={{
                popper: {
                  sx: sxStyles.tooltipPopper,
                },
              }}
              title={
                <Box sx={sxStyles.tooltipContent}>
                  <span>Key insights:</span>
                  <List
                    sx={{
                      listStyleType: "disc",
                      paddingLeft: "0.5rem",
                      paddingTop: "0",
                      paddingBottom: "0",
                    }}
                  >
                    {Object.keys(insightTypes).map((i) => (
                      <ListItem
                        key={i}
                        sx={{ display: "list-item" }}
                        disablePadding
                      >
                        {i}: {insightTypes[i]}
                      </ListItem>
                    ))}
                  </List>
                </Box>
              }
              arrow
            >
              <Chip
                icon={
                  <Insights
                    sx={{
                      height: "1rem",
                      width: "1rem",
                      color: `${green[800]} !important`,
                    }}
                  />
                }
                label={quoteCount}
                sx={{
                  backgroundColor: green[50],
                  color: green[800],
                  padding: "0.25rem 0.5rem 0.25rem 0.5rem",
                }}
              />
            </Tooltip>
          );
        }
        return <></>;
      },
    },
    {
      field: "clinical_insights",
      headerName: "Clinical Insights",
      display: "flex",
      width: 100,
      sortable: false,
      valueGetter: (value, row) => {
        const clinicalInsights = sortClinicalInsights(
          row.clinical_insights ?? [],
        );
        const displayNames = clinicalInsights.map(
          (ci) => ci.milestone.display_name,
        );
        const values = clinicalInsights.map((ci) =>
          clinicalInsightPrimaryValue(ci),
        );
        return displayNames.concat(values);
      },
      renderCell: (params) => {
        const clinicalInsights = sortClinicalInsights(
          params.row.clinical_insights ?? [],
        );

        return clinicalInsights.length > 0 ? (
          <Tooltip
            slotProps={{
              popper: {
                sx: sxStyles.tooltipPopper,
              },
            }}
            title={
              <Box sx={sxStyles.tooltipContent}>
                <List
                  sx={{
                    listStyleType: "disc",
                    paddingLeft: "0.5rem",
                    paddingTop: "0",
                    paddingBottom: "0",
                  }}
                >
                  {clinicalInsights.map((i) => {
                    const primary = clinicalInsightPrimaryValue(i);
                    const tsRaw = clinicalInsightTimestampRaw(i);
                    const tsMoment = tsRaw ? moment(tsRaw) : null;
                    const tsLabel =
                      tsMoment && tsMoment.isValid()
                        ? tsMoment.format("MM/DD/YYYY")
                        : "";
                    return (
                      <ListItem
                        key={i.id}
                        sx={{
                          display: "list-item",
                          paddingBottom: 0,
                          paddingTop: 0,
                        }}
                        disableGutters
                      >
                        <span>{i.milestone.display_name}</span>
                        {primary && primary !== "-" && (
                          <span> - {primary}</span>
                        )}
                        {tsLabel ? <span> ({tsLabel})</span> : null}
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
            }
            arrow
          >
            <Chip
              icon={
                <AutoAwesomeOutlined
                  sx={{
                    height: "1rem",
                    width: "1rem",
                    color: `${indigo[700]} !important`,
                  }}
                />
              }
              label={clinicalInsights.length}
              sx={{
                backgroundColor: indigo[50],
                color: indigo[800],
                padding: "0.25rem 0.5rem 0.25rem 0.5rem",
              }}
            />
          </Tooltip>
        ) : (
          <></>
        );
      },
    },
  ];

  // Remove columns based on config
  const finalColumns = baseColumns.filter((col) => {
    if (
      (col.field === "payor" && !configuration.payor_display_enabled) ||
      (col.field === "insights" && !configuration.insights_section_enabled) ||
      (col.field === "clinical_insights" &&
        !configuration.clinical_insights_display_enabled) ||
      (col.field === "discharge_order_status" &&
        !configuration.discharge_order_status_display_enabled)
    ) {
      return false;
    }
    return true;
  });

  return finalColumns as GridColDef[];
};
